import { useRef, useState, DragEvent } from 'react';
import { ArrowLeftIcon, ArrowUpIcon, XIcon } from 'lucide-react';
import { Mission } from '../../data/missions';
import HowToUse from '../HowToUse/HowToUse';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { generateHrConsolidation } from '../../api/hrConsolidatorApi';
import { useFileRun } from '../../hooks/useFileRun';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload the Pay Register, New Joinee Data and Education Mapping files (all required).',
  'Optionally add the Previous Month Consolidated and Exit Record files, and set an output filename.',
  'Hit Generate Consolidation — the consolidated HR workbook downloads automatically.',
];

const INPUTS: {
  key: FileKey;
  label: string;
  required: boolean;
  note: string;
}[] = [
  {
    key: 'payRegisterFile',
    label: 'Pay Register',
    required: true,
    note: 'Monthly pay register export (.xlsx / .xls).',
  },
  {
    key: 'newJoineeFile',
    label: 'New Joinee Data',
    required: true,
    note: 'New joiners for the period.',
  },
  {
    key: 'educationFile',
    label: 'Education Mapping',
    required: true,
    note: 'Education / qualification mapping reference.',
  },
  {
    key: 'previousMonthFile',
    label: 'Previous Month Consolidated',
    required: false,
    note: 'Optional — the consolidator is built from scratch if omitted.',
  },
  {
    key: 'exitRecordFile',
    label: 'Exit Record',
    required: false,
    note: 'Optional — Active / Exit status is skipped if omitted.',
  },
];

type FileKey =
  | 'payRegisterFile'
  | 'newJoineeFile'
  | 'educationFile'
  | 'previousMonthFile'
  | 'exitRecordFile';

type FileState = Record<FileKey, File | null>;

interface HrConsolidatorPanelProps {
  mission: Mission;
  onBack: () => void;
}

interface FileDropProps {
  label: string;
  required: boolean;
  note: string;
  accept: string;
  hint: string;
  accentColor: string;
  file: File | null;
  onFile: (file: File | null) => void;
}

function FileDrop({
  label,
  required,
  note,
  accept,
  hint,
  accentColor,
  file,
  onFile,
}: FileDropProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  return (
    <div className="mb-4">
      <Label className="mb-1 text-sm font-semibold text-foreground">
        {label} {required ? <span className="text-red-600">*</span> : <span className="text-muted-foreground">(optional)</span>}
      </Label>
      <p className="mb-2 text-xs text-muted-foreground">{note}</p>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e: DragEvent<HTMLDivElement>) => {
          e.preventDefault();
          setDragActive(false);
          const dropped = e.dataTransfer.files && e.dataTransfer.files[0];
          if (dropped) onFile(dropped);
        }}
        className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-6 text-center transition-colors ${
          dragActive ? 'border-current bg-muted' : 'border-input bg-muted'
        }`}
        style={dragActive ? { borderColor: accentColor } : undefined}
      >
        <Button
          onClick={() => inputRef.current?.click()}
          style={{ backgroundColor: accentColor }}
          className="text-white hover:opacity-90"
        >
          <ArrowUpIcon /> Upload file
        </Button>
        <span className="text-xs text-muted-foreground">or drag and drop a file here</span>
        <span className="text-xs text-muted-foreground">{hint}</span>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          hidden
          onChange={(e) => {
            const picked = e.target.files && e.target.files[0];
            if (picked) onFile(picked);
            e.target.value = '';
          }}
        />
      </div>
      {file && (
        <div className="mt-2 flex items-center justify-between rounded-lg bg-green-50 px-4 py-2.5 text-sm text-green-700">
          <span>📄 {file.name}</span>
          <button
            type="button"
            onClick={() => onFile(null)}
            className="text-green-700 hover:text-green-900"
            aria-label={`Remove ${label} file`}
          >
            <XIcon className="size-4" />
          </button>
        </div>
      )}
    </div>
  );
}

function HrConsolidatorPanel({ mission, onBack }: HrConsolidatorPanelProps) {
  const [files, setFiles] = useState<FileState>({
    payRegisterFile: null,
    newJoineeFile: null,
    educationFile: null,
    previousMonthFile: null,
    exitRecordFile: null,
  });
  const [outputFilename, setOutputFilename] = useState('HR_Consolidated.xlsx');
  const { status, error, result, fail, run } = useFileRun<{ blob: Blob; filename: string }>();

  const setFile = (key: FileKey) => (file: File | null) =>
    setFiles((prev) => ({ ...prev, [key]: file }));

  const handleSubmit = async () => {
    // Local copies so the null check below still applies inside the closure passed to `run`.
    const { payRegisterFile, newJoineeFile, educationFile, previousMonthFile, exitRecordFile } = files;
    if (!payRegisterFile || !newJoineeFile || !educationFile) {
      fail('Upload the Pay Register, New Joinee Data and Education Mapping files before generating.');
      return;
    }
    await run(
      () =>
        generateHrConsolidation(
          { payRegisterFile, newJoineeFile, educationFile, previousMonthFile, exitRecordFile },
          { outputFilename },
        ),
      'Consolidation failed.',
    );
  };

  const accept = '.xlsx,.xls';

  return (
    <section
      style={{ borderTopColor: mission.color }}
      className="mx-auto max-w-[700px] rounded-2xl border border-border border-t-[6px] bg-card p-8 shadow-sm"
    >
      <Button
        variant="ghost"
        onClick={onBack}
        style={{ color: mission.color }}
        className="mb-5 h-auto gap-1.5 px-2 py-1.5 text-sm font-semibold hover:bg-transparent"
      >
        <ArrowLeftIcon className="size-5" /> All missions
      </Button>

      <div className="mb-6 flex items-start gap-4">
        <span
          style={{ backgroundColor: `${mission.color}26`, color: mission.color }}
          className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl"
        >
          <mission.icon className="h-7 w-7" />
        </span>
        <div>
          <h2 className="mb-1 text-xl font-bold text-foreground">{mission.title}</h2>
          <p className="text-sm text-muted-foreground">{mission.description}</p>
        </div>
      </div>

      <HowToUse steps={STEPS} />

      <div className="mb-5 rounded-lg border border-border bg-muted p-4">
        <p className="mb-2 text-sm font-semibold text-foreground">📝 Expected inputs</p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>
            <strong>Pay Register</strong>, <strong>New Joinee Data</strong> and{' '}
            <strong>Education Mapping</strong> are mandatory.
          </li>
          <li>
            <strong>Previous Month Consolidated</strong> is optional — the file is built from scratch
            when omitted.
          </li>
          <li>
            <strong>Exit Record</strong> is optional — Active / Exit status is skipped when omitted.
          </li>
          <li>All files must be Excel workbooks (.xlsx / .xls).</li>
        </ul>
      </div>

      {INPUTS.map((input) => (
        <FileDrop
          key={input.key}
          label={input.label}
          required={input.required}
          note={input.note}
          accept={accept}
          hint={mission.maxSizeLabel}
          accentColor={mission.color}
          file={files[input.key]}
          onFile={setFile(input.key)}
        />
      ))}

      <div className="mb-6 mt-1 rounded-lg border border-border bg-muted p-4">
        <Label htmlFor="output-filename" className="mb-1.5">
          Output filename
        </Label>
        <Input
          id="output-filename"
          value={outputFilename}
          onChange={(e) => setOutputFilename(e.target.value)}
          placeholder="HR_Consolidated.xlsx"
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Generating…' : 'Generate Consolidation'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Generating…'} />}

      {status === 'error' && error && (
        <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">⚠️ {error}</div>
      )}
      {status === 'success' && result && (
        <div className="mt-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
          ✅ {result.filename} downloaded.
        </div>
      )}
      {status === 'idle' && (
        <div className="mt-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-700">
          📁 Upload the three required files above to get started.
        </div>
      )}
    </section>
  );
}

export default HrConsolidatorPanel;
